import { Button, Input, Select, toast } from "@gotts.ai/ui";
import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { encodeFunctionData, formatUnits, parseUnits } from "viem";

import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment, useEvmBackend, useWallet } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { MOCK_ERC20_ABI } from "../../lib/abis";
import { TOKEN_DECIMALS } from "../../lib/constants";
import { formatDisplayAmount } from "../../lib/format";

import { DEFAULT_MINT_PRESETS, TOKEN_MINT_PRESETS } from "./constants";

import type { ChainControlSharedProps } from "./types";
import type { Address } from "viem";

const MINTABLE_TOKENS = [
  { label: "USDC", value: "usdc" },
  { label: "USDT", value: "usdt" },
  { label: "DAI", value: "dai" },
  { label: "WBTC", value: "wbtc" },
] as const;

export function MintTokensCard({
  loading,
  withLoading,
  onAction,
  blockNumber,
}: ChainControlSharedProps) {
  const { request } = useRpcRequest();
  const { deployment } = useDeployment();
  const { address: connectedAddress } = useWallet();
  const { accounts } = useEvmBackend();

  const accountOptions = useMemo(
    () => [
      ...accounts.map((a) => ({
        label: `${a.label} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
        value: a.address,
      })),
      { label: "Other", value: "__other__" },
    ],
    [accounts],
  );

  const [mintToken, setMintToken] = useState("usdc");
  const [accountSelect, setAccountSelect] = useState<string>(
    accounts[0]?.address ?? "",
  );
  const [customRecipient, setCustomRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [currentBalance, setCurrentBalance] = useState<string | null>(null);

  const recipient =
    accountSelect === "__other__" ? customRecipient : accountSelect;
  const resolvedRecipient = (recipient || connectedAddress) as
    | Address
    | undefined;

  const symbol = mintToken.toUpperCase();
  const decimals = TOKEN_DECIMALS[symbol] ?? 18;

  const fetchBalance = useCallback(async () => {
    if (!resolvedRecipient || !deployment?.tokens) {
      setCurrentBalance(null);
      return;
    }
    const tokenAddress = deployment.tokens[
      mintToken as keyof typeof deployment.tokens
    ] as Address | undefined;
    if (!tokenAddress) {
      setCurrentBalance(null);
      return;
    }
    try {
      const calldata = encodeFunctionData({
        abi: MOCK_ERC20_ABI,
        functionName: "balanceOf",
        args: [resolvedRecipient],
      });
      const raw = (await request("eth_call", [
        { to: tokenAddress, data: calldata },
        "latest",
      ])) as string | null;
      if (raw && raw !== "0x") {
        setCurrentBalance(formatUnits(BigInt(raw), decimals));
      } else {
        setCurrentBalance("0");
      }
    } catch {
      setCurrentBalance(null);
    }
  }, [resolvedRecipient, deployment, mintToken, decimals, request]);

  useEffect(() => {
    void fetchBalance();
  }, [fetchBalance, blockNumber]);

  const accountLabel =
    accounts.find((a) => a.address === resolvedRecipient)?.label ??
    (resolvedRecipient
      ? `${resolvedRecipient.slice(0, 6)}...${resolvedRecipient.slice(-4)}`
      : "unknown");

  async function handleMint() {
    if (!resolvedRecipient || !amount || !deployment?.tokens) return;

    const tokenAddress = deployment.tokens[
      mintToken as keyof typeof deployment.tokens
    ] as Address | undefined;
    if (!tokenAddress) return;

    await withLoading("mint", async () => {
      const parsedAmount = parseUnits(amount, decimals);

      const calldata = encodeFunctionData({
        abi: MOCK_ERC20_ABI,
        functionName: "mint",
        args: [resolvedRecipient, parsedAmount],
      });

      const deployer = accounts[0]?.address;
      if (!deployer) return;
      await request("anvil_impersonateAccount", [deployer]);
      await request("eth_sendTransaction", [
        {
          from: deployer,
          to: tokenAddress,
          data: calldata,
        },
      ]);
      await request("anvil_stopImpersonatingAccount", [deployer]);

      // Fetch new balance for toast
      let newBal = "";
      try {
        const cd = encodeFunctionData({
          abi: MOCK_ERC20_ABI,
          functionName: "balanceOf",
          args: [resolvedRecipient],
        });
        const raw = (await request("eth_call", [
          { to: tokenAddress, data: cd },
          "latest",
        ])) as string | null;
        if (raw && raw !== "0x") {
          newBal = `. New balance: ${formatDisplayAmount(formatUnits(BigInt(raw), decimals)).display} ${symbol}`;
        }
      } catch {
        // ignore
      }

      toast.success(
        `Minted ${formatDisplayAmount(amount).display} ${symbol} to ${accountLabel}${newBal}`,
      );
      onAction?.(`Minted ${amount} ${symbol}`);
    });
  }

  return (
    <div className="space-y-4">
      {/* Token + Account selectors */}
      <div className="flex gap-2">
        <div className="flex items-center gap-1.5 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] px-2.5 py-1.5">
          <TokenIcon symbol={symbol} size={20} />
          <Select
            options={MINTABLE_TOKENS.map((t) => ({
              label: t.label,
              value: t.value,
            }))}
            value={mintToken}
            onValueChange={setMintToken}
            placeholder="Token"
            className="w-20"
          />
        </div>
        <Select
          options={accountOptions}
          value={accountSelect}
          onValueChange={setAccountSelect}
          placeholder="Recipient"
          className="flex-1"
        />
      </div>

      {accountSelect === "__other__" && (
        <Input
          type="text"
          placeholder={connectedAddress ?? "0x... custom recipient"}
          value={customRecipient}
          onChange={(e) => {
            setCustomRecipient(e.target.value);
          }}
          className="w-full"
        />
      )}

      {/* Balance display — hero serif number */}
      {currentBalance !== null && (
        <div className="flex items-baseline gap-2">
          <span className="hero-value text-xl">
            {formatDisplayAmount(currentBalance).display}
          </span>
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            {symbol}
          </span>
        </div>
      )}

      {/* Amount + Mint */}
      <div className="flex gap-2">
        <Input
          type="number"
          placeholder="Amount"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
          }}
          className="flex-1"
        />
        <Button
          variant="gold"
          size="sm"
          onClick={handleMint}
          loading={loading === "mint"}
          disabled={
            loading !== null ||
            !amount ||
            (!recipient && !connectedAddress) ||
            !deployment?.tokens
          }
        >
          Mint
        </Button>
      </div>

      {/* Before -> After preview */}
      {currentBalance !== null && amount && parseFloat(amount) > 0 && (
        <div className="flex items-center gap-2 rounded-sm bg-[rgba(255,255,255,0.02)] px-3 py-2">
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {formatDisplayAmount(currentBalance).display} {symbol}
          </span>
          <ArrowRight size={12} className="text-[var(--color-gold)]/50" />
          <span className="font-mono text-[11px] text-[var(--color-text)]">
            {
              formatDisplayAmount(
                String(parseFloat(currentBalance) + parseFloat(amount)),
              ).display
            }{" "}
            {symbol}
          </span>
        </div>
      )}

      {/* Presets */}
      <div className="flex flex-wrap gap-1.5">
        {(TOKEN_MINT_PRESETS[symbol] ?? DEFAULT_MINT_PRESETS).map((p) => (
          <button
            key={p}
            type="button"
            className="btn-percentage-pill"
            onClick={() => {
              setAmount(p);
            }}
            disabled={loading !== null}
          >
            {Number(p).toLocaleString()}
          </button>
        ))}
      </div>
    </div>
  );
}
